import * as React from 'react';
import { ComboBox, Item, Flex, View, Text } from '@adobe/react-spectrum';
import { useAsyncList } from '@react-stately/data';
import type { Key } from '@react-types/shared';
import { IFilterControl, IFilterItem } from '../../FilterSetupPlaceholder';
import { useDependency } from '../../../../../../../contexts/DependencyProvider';
import { ListFPARequest2 } from '../../../../../../../services/soap/project/requests/ListFPARequest2';
import { useFilterGuard } from '../../../../../../../hooks/useFilterGuard';
import styles from './FolderFilterControl.module.css';

/**
 * Props for the FolderFilterControl component
 */
export interface IFolderFilterControlProps {
  /** Filter control configuration object */
  filter: IFilterControl;
  /** Callback function called when filter values change */
  onFilterChange: (filter: IFilterControl) => void;
  /** Optional FPA type category for filtering folders */
  fpaTypeCategory?: string;
}

interface Folder {
  id: number;
  name: string;
}

export const FolderFilterControl = React.forwardRef<HTMLDivElement, IFolderFilterControlProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange, fpaTypeCategory } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const { projectService, store } = useDependency();
    const apiDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
    // Guard against effect-triggered update loops when normalizing legacy operators.
    const isInternalUpdateRef = React.useRef(false);
    const lastProcessedFilterInfoSignatureRef = React.useRef<string>('');

    const currentFilterItem = React.useMemo(
      () =>
        filter.filterInfo[0] || {
          name: 'value',
          type: 'in',
          value: '',
        },
      [filter.filterInfo]
    );

    React.useEffect(() => {
      syncGuard(filter);
    }, [filter, syncGuard]);

    const [selectedKey, setSelectedKey] = React.useState<Key | null>(
      currentFilterItem.value ? String(currentFilterItem.value) : null
    );
    const [inputValue, setInputValue] = React.useState<string>(
      currentFilterItem.valueLabel || ''
    );

    // Normalize folder operator to 'in' (folder selection is an ID-membership filter).
    // This prevents legacy/default 'contains' (or 'equals') operators from leaking into API params.
    React.useEffect(() => {
      const currentItem = filter.filterInfo[0];
      const signature = JSON.stringify({
        type: currentItem?.type,
        value: currentItem?.value,
        valueLabel: currentItem?.valueLabel,
        name: currentItem?.name,
      });

      // Skip if we've already processed this exact state.
      if (lastProcessedFilterInfoSignatureRef.current === signature) {
        return;
      }

      // Skip one cycle when this component just emitted an update.
      if (isInternalUpdateRef.current) {
        isInternalUpdateRef.current = false;
        lastProcessedFilterInfoSignatureRef.current = signature;
        return;
      }

      if (!currentItem) {
        lastProcessedFilterInfoSignatureRef.current = signature;
        return;
      }

      const needsConversion =
        !currentItem.type ||
        currentItem.type === 'equals' ||
        currentItem.type === 'not_equals' ||
        currentItem.type === 'contains';

      if (!needsConversion) {
        lastProcessedFilterInfoSignatureRef.current = signature;
        return;
      }

      const updatedFilterInfo: IFilterItem[] = [
        {
          ...currentItem,
          type: 'in',
        },
      ];

      // Mark the next signature as processed before emitting to avoid reprocessing.
      lastProcessedFilterInfoSignatureRef.current = JSON.stringify({
        type: 'in',
        value: currentItem?.value,
        valueLabel: currentItem?.valueLabel,
        name: currentItem?.name,
      });
      const didNotify = notifyChange({
        ...filter,
        filterInfo: updatedFilterInfo,
      });
      if (didNotify) {
        isInternalUpdateRef.current = true;
      }
    }, [filter, filter.filterInfo, notifyChange]);

    // Sync with filter changes from parent
    React.useEffect(() => {
      const currentItem = filter.filterInfo[0];
      if (currentItem) {
        setSelectedKey(currentItem.value ? String(currentItem.value) : null);
        setInputValue(currentItem.valueLabel || '');
      }
    }, [filter.filterInfo]);

    const list = useAsyncList<Folder>({
      async load({ cursor, filterText }) {
        // Don't search if filterText is empty or too short
        if (!filterText || filterText.trim().length < 2) {
          return { items: [], cursor: undefined };
        }

        // Clear any existing debounce timeout
        if (apiDebounceRef.current) {
          clearTimeout(apiDebounceRef.current);
        }

        // Debounce API calls by 500ms
        return new Promise((resolve) => {
          apiDebounceRef.current = setTimeout(async () => {
            try {
              const offset = cursor ? Number(cursor) : 0;
              const limit = 50;

              const response = await projectService.listFPA2(
                new ListFPARequest2(store.Server, store.SessionId, {
                  limit: limit,
                  offset: offset,
                  flatView: true,
                  fulltext: filterText + '*',
                  folderFilter: fpaTypeCategory ? {
                    COL: [{
                      name: 'fpaTypeCategories',
                      type: 'any',
                      value: fpaTypeCategory
                    }]
                  } : undefined,
                  ORDER: {
                    COL: [
                      {
                        name: 'name',
                        desc: false as any,
                      },
                      {
                        name: 'created',
                        desc: true as any,
                      },
                    ],
                  },
                })
              );

              // Extract only FOLDER items from response
              const folders: Folder[] =
                response?.ITEMS?.map((entry: any) => entry.ITEM?.FOLDER).filter((f: Folder) => f) || [];

              // Map to required format
              const mappedFolders: Folder[] = folders.map((folder: any) => ({
                id: folder.id,
                name: folder.name || String(folder.id),
              }));

              resolve({
                items: mappedFolders,
                cursor: mappedFolders.length === limit ? String(offset + limit) : undefined,
              });
            } catch (error) {
              console.error('Error fetching folders:', error);
              resolve({ items: [], cursor: undefined });
            }
          }, 500);
        });
      },
    });

    const handleSelectionChange = React.useCallback(
      (key: Key | null) => {
        setSelectedKey(key);

        if (key === null) {
          // Clear the filter
          const updatedFilterInfo: IFilterItem[] = [
            {
              ...currentFilterItem,
              type: 'in',
              value: '',
              valueLabel: '',
            },
          ];
          notifyChange({
            ...filter,
            filterInfo: updatedFilterInfo,
          });
          setInputValue('');
        } else {
          // Find selected folder in the list - it should always be there when selected from dropdown
          const selectedFolder = list.items.find((f) => String(f.id) === String(key));
          if (selectedFolder) {
            const updatedFilterInfo: IFilterItem[] = [
              {
                ...currentFilterItem,
                type: 'in',
                value: String(selectedFolder.id),
                valueLabel: selectedFolder.name,
              },
            ];
            
            notifyChange({
              ...filter,
              filterInfo: updatedFilterInfo,
            });
            
            setInputValue(selectedFolder.name);
          } else {
            // Folder not found in current list - this shouldn't happen normally, but handle it gracefully
            // Preserve existing valueLabel if we have one, otherwise use empty string
            console.warn(`Folder with id ${key} not found in current list`);
            const updatedFilterInfo: IFilterItem[] = [
              {
                ...currentFilterItem,
                type: 'in',
                value: String(key),
                valueLabel: currentFilterItem.name || '',
              },
            ];
            
            notifyChange({
              ...filter,
              filterInfo: updatedFilterInfo,
            });
          }
        }
      },
      [filter, currentFilterItem, notifyChange, list.items]
    );

    const handleInputChange = React.useCallback(
      (value: string) => {
        setInputValue(value);
        // Clear selection if input is cleared
        if (!value) {
          setSelectedKey(null);
          const updatedFilterInfo: IFilterItem[] = [
            {
              ...currentFilterItem,
              type: 'in',
              value: '',
              valueLabel: '',
            },
          ];
          notifyChange({
            ...filter,
            filterInfo: updatedFilterInfo,
          });
        }
        list.setFilterText(value);
      },
      [filter, currentFilterItem, notifyChange, list]
    );

    // Cleanup debounce on unmount
    React.useEffect(() => {
      return () => {
        if (apiDebounceRef.current) {
          clearTimeout(apiDebounceRef.current);
        }
      };
    }, []);

    return (
      <div ref={forwardedRef} className={styles.folderFilterControl}>
        <Flex direction="column" gap="size-200">
          {/* Label row */}
          <Text UNSAFE_className={styles.label}>{filter.label || filter.name}</Text>
          
          {/* ComboBox row */}
          <View width="100%">
            <ComboBox
              aria-label="Folder search"
              items={list.items}
              selectedKey={selectedKey}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onSelectionChange={handleSelectionChange}
              loadingState={list.loadingState}
              onLoadMore={list.loadMore}
              allowsCustomValue={false}
              width="100%"
              description="Type to search folders..."
            >
              {(item) => (
                <Item key={String(item.id)} textValue={item.name}>
                  {item.name}
                </Item>
              )}
            </ComboBox>
          </View>
        </Flex>
      </div>
    );
  }
);

FolderFilterControl.displayName = 'FolderFilterControl';

